import type { SearchFilters, Lead, ResearchedContact, ScheduledSearch, SavedSearch } from '@/types/research';
import { API_CONFIG } from './api';
import { findMatchingResearchedContact } from '@/utils/researchIdentity';
import { supabase } from './supabase';

const SEARCH_API_URL = API_CONFIG.SEARCH_API;
const RESEARCH_API_URL = API_CONFIG.RESEARCH_API;
const TRANSIENT_RESEARCH_STATUSES = new Set([429, 502, 503, 504]);

function isTransientResearchStatus(status: number): boolean {
  return TRANSIENT_RESEARCH_STATUSES.has(status);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readErrorDetails(response: Response): Promise<string> {
  try {
    const text = (await response.text()).trim();
    if (!text) return '';
    try {
      const parsed = JSON.parse(text) as { error?: unknown; message?: unknown };
      const candidate = String(parsed.error ?? parsed.message ?? '').trim();
      return candidate || text;
    } catch {
      return text;
    }
  } catch {
    return '';
  }
}

function formatHttpError(label: string, status: number, details: string): string {
  return `${label} (${status})${details ? `: ${details}` : ''}`;
}

export type InstantlyCampaignOption = {
  id: string;
  name: string;
};

export type InstantlyCampaignStepInput = {
  subject: string;
  body: string;
  delayDays?: number;
};

export type InstantlyCampaignCreateInput = {
  userEmail: string;
  name: string;
  timezone?: string;
  startDate?: string;
  endDate?: string;
  dailyLimit?: number;
  emailGapMinutes?: number;
  stopOnReply?: boolean;
  openTracking?: boolean;
  linkTracking?: boolean;
  steps: InstantlyCampaignStepInput[];
};

export type InstantlyCampaignUpdateInput = {
  userEmail: string;
  campaignId: string;
  name: string;
  timezone?: string;
  startDate?: string;
  endDate?: string;
  dailyLimit?: number;
  emailGapMinutes?: number;
  stopOnReply?: boolean;
  openTracking?: boolean;
  linkTracking?: boolean;
  steps: InstantlyCampaignStepInput[];
};

function leadCompanyName(lead: Lead): string {
  if (typeof lead.company === 'string') return lead.company;
  return lead.company?.name || lead.companyName || lead.Company || '';
}

function leadCompanyLinkedIn(lead: Lead): string {
  if (typeof lead.company === 'string') return lead.companyLinkedInUrl || lead.CompanyLIProfileUrl || '';
  return lead.company?.linkedInUrl || lead.companyLinkedInUrl || lead.CompanyLIProfileUrl || '';
}

function leadPrimaryEmail(lead: Lead): string {
  const candidates = [
    (lead as { email?: unknown }).email,
    (lead as { Email?: unknown }).Email,
    (lead as { businessEmail?: unknown }).businessEmail,
    (lead as { BusinessEmail?: unknown }).BusinessEmail,
    (lead as { personalEmail?: unknown }).personalEmail,
    (lead as { PersonalEmail?: unknown }).PersonalEmail,
    (lead as { email1?: unknown }).email1,
    (lead as { Email1?: unknown }).Email1,
  ];
  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (value.includes('@')) return value;
  }
  return '';
}

function buildManualResearchTags(lead: Lead, userEmail: string, extraTags: string[] = []): string[] {
  const tags: string[] = [];
  const priorityTags: string[] = [];
  const push = (raw: unknown, prefix?: string, priority = false) => {
    const value = String(raw ?? '').trim();
    if (!value) return;
    const tag = prefix ? `${prefix} - ${value}` : value;
    const target = priority ? priorityTags : tags;
    if (!priorityTags.includes(tag) && !tags.includes(tag)) target.push(tag);
  };

  push('manual get data', 'type');
  push(leadCompanyName(lead), 'company name');
  push(lead.title, 'title');
  push(lead.industry || (typeof lead.company === 'object' ? lead.company?.industry : ''), 'industry');
  push(lead.location || (typeof lead.company === 'object' ? lead.company?.location : ''), 'location');
  push(lead.city, 'city');
  push(lead.state, 'state');
  push(userEmail, 'user email');

  for (const tag of extraTags) {
    const value = String(tag ?? '').trim();
    if (!value) continue;
    const isPepai = value.toLowerCase() === 'pepai-research';
    push(value, undefined, isPepai);
  }

  const leadLists = Array.isArray((lead as { lists?: unknown }).lists)
    ? ((lead as { lists?: string[] }).lists ?? [])
    : [];
  for (const tag of leadLists) push(tag);

  return [...priorityTags, ...tags].slice(0, 50);
}

function normalizeKeyPart(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16);
}

function stableLeadKey(lead: Record<string, unknown>, fallbackIndex: number): string {
  const linkedin =
    (lead as { liUrl?: unknown }).liUrl ??
    (lead as { linkedInUrl?: unknown }).linkedInUrl ??
    (lead as { linkedin?: unknown }).linkedin ??
    (lead as { LIProfileUrl?: unknown }).LIProfileUrl ??
    (lead as { linkedinUrl?: unknown }).linkedinUrl;
  const email =
    (lead as { email?: unknown }).email ??
    (lead as { Email?: unknown }).Email ??
    (lead as { businessEmail?: unknown }).businessEmail ??
    (lead as { BusinessEmail?: unknown }).BusinessEmail ??
    (lead as { personalEmail?: unknown }).personalEmail ??
    (lead as { PersonalEmail?: unknown }).PersonalEmail ??
    (lead as { email1?: unknown }).email1 ??
    (lead as { Email1?: unknown }).Email1;
  const name =
    (lead as { name?: unknown }).name ??
    (lead as { fullName?: unknown }).fullName ??
    `${(lead as { firstName?: unknown }).firstName ?? ''} ${(lead as { lastName?: unknown }).lastName ?? ''}`.trim();
  const company = leadCompanyName(lead as unknown as Lead);
  const contactSearchId =
    (lead as { contactSearchId?: unknown }).contactSearchId ??
    (lead as { contact_search_id?: unknown }).contact_search_id ??
    '';

  const linkedinKey = normalizeKeyPart(linkedin);
  if (linkedinKey) return `li_${fnv1a(linkedinKey)}`;

  const emailKey = normalizeKeyPart(email);
  if (emailKey) return `em_${fnv1a(emailKey)}`;

  const nameKey = normalizeKeyPart(name);
  const companyKey = normalizeKeyPart(company);
  if (nameKey || companyKey) return `nc_${fnv1a(`${nameKey}|${companyKey}`)}`;

  const searchKey = normalizeKeyPart(contactSearchId);
  if (searchKey) return `cs_${searchKey}_${fallbackIndex}`;

  return `lead_${fallbackIndex}`;
}

function normalizeSearchResultId(lead: Record<string, unknown>, fallbackIndex: number): string {
  const raw =
    (lead as { searchResultId?: unknown }).searchResultId ??
    (lead as { SearchResultId?: unknown }).SearchResultId ??
    (lead as { search_result_id?: unknown }).search_result_id ??
    (lead as { searchResultID?: unknown }).searchResultID ??
    (lead as { SearchResultID?: unknown }).SearchResultID ??
    (lead as { id?: unknown }).id ??
    (lead as { _id?: unknown })._id ??
    (lead as { uiMatchId?: unknown }).uiMatchId ??
    (lead as { uiMatchID?: unknown }).uiMatchID;
  const normalized = String(raw ?? '').trim();
  if (normalized) return normalized;
  return stableLeadKey(lead, fallbackIndex);
}

function normalizeUserEmail(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function buildSearchIdVariants(value: unknown): string[] {
  const raw = String(value ?? '').trim();
  if (!raw) return [];
  const variants = new Set<string>([raw]);
  const stripped = raw.replace(/__\d+$/, '');
  if (stripped) variants.add(stripped);
  return Array.from(variants);
}

function mapSupabaseRowToResearchedContact(row: any): ResearchedContact {
  const fullName = String(row?.full_name ?? `${row?.first_name ?? ''} ${row?.last_name ?? ''}`.trim()).trim();
  const searchResultId = String(row?.search_result_id ?? row?.contact_id ?? row?.id ?? '').trim();
  const contactId = String(row?.contact_id ?? row?.id ?? '').trim();
  return {
    ...(searchResultId ? { searchResultId } : {}),
    ...(searchResultId ? { sourceSearchResultId: searchResultId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(contactId ? { id: contactId } : {}),
    ...(fullName ? { fullName, Name: fullName } : {}),
    ...(row?.first_name ? { firstName: row.first_name } : {}),
    ...(row?.last_name ? { lastName: row.last_name } : {}),
    ...(row?.title ? { title: row.title, Title: row.title } : {}),
    ...(row?.company ? { company: row.company, Company: row.company } : {}),
    ...(row?.li_profile_url ? { LIProfileUrl: row.li_profile_url } : {}),
    ...(row?.email1 || row?.selected_email ? { Email: row.email1 || row.selected_email } : {}),
    ...(row?.personal_email ? { PersonalEmail: row.personal_email } : {}),
    ...(row?.contact_phone1 ? { contactPhone1: row.contact_phone1 } : {}),
    ...(row?.company_phone1 ? { companyPhone1: row.company_phone1 } : {}),
  };
}

async function hydrateResearchedDataForContacts(
  userEmail: string | undefined,
  contacts: Lead[]
): Promise<Lead[]> {
  const normalizedUserEmail = normalizeUserEmail(userEmail);
  if (!normalizedUserEmail || contacts.length === 0) return contacts;

  const leadIdsByVariant = new Map<string, number[]>();
  contacts.forEach((lead, index) => {
    const sourceId =
      (lead as { sourceSearchResultId?: unknown }).sourceSearchResultId ??
      (lead as { searchResultIdRaw?: unknown }).searchResultIdRaw ??
      (lead as { search_result_id?: unknown }).search_result_id ??
      (lead as { SearchResultId?: unknown }).SearchResultId ??
      lead.searchResultId;
    for (const variant of buildSearchIdVariants(sourceId)) {
      const existing = leadIdsByVariant.get(variant) ?? [];
      existing.push(index);
      leadIdsByVariant.set(variant, existing);
    }
  });

  const variantIds = Array.from(leadIdsByVariant.keys()).filter(Boolean);
  if (variantIds.length === 0) return contacts;

  const researchedByVariant = new Map<string, ResearchedContact>();
  const isTimeoutLikeSupabaseError = (message: string) => {
    const lower = String(message || '').toLowerCase();
    return (
      lower.includes('statement timeout') ||
      lower.includes('canceling statement due to statement timeout') ||
      lower.includes('"code":"57014"')
    );
  };
  const loadRowsBySearchResultIds = async (ids: string[]): Promise<any[]> => {
    const unique = Array.from(new Set(ids.map((id) => String(id || '').trim()).filter(Boolean)));
    if (!unique.length) return [];
    const query = async (chunk: string[]): Promise<any[]> => {
      if (!chunk.length) return [];
      const { data, error } = await supabase
        .from('seamless_data')
        .select(
          'id,contact_id,search_result_id,full_name,first_name,last_name,title,company,li_profile_url,email1,selected_email,personal_email,contact_phone1,company_phone1'
        )
        .eq('user_email', normalizedUserEmail)
        .in('search_result_id', chunk);
      if (!error) return Array.isArray(data) ? data : [];
      if (chunk.length <= 1 || !isTimeoutLikeSupabaseError(error.message)) {
        console.warn('hydrateResearchedDataForContacts failed', { message: error.message, chunkSize: chunk.length });
        return [];
      }
      const splitAt = Math.floor(chunk.length / 2);
      const left = await query(chunk.slice(0, splitAt));
      const right = await query(chunk.slice(splitAt));
      return [...left, ...right];
    };
    return query(unique);
  };

  const CHUNK_SIZE = 40;
  for (let i = 0; i < variantIds.length; i += CHUNK_SIZE) {
    const chunk = variantIds.slice(i, i + CHUNK_SIZE);
    const rows = await loadRowsBySearchResultIds(chunk);
    for (const row of rows as any[]) {
      const rowSearchResultId = String((row as any)?.search_result_id ?? '').trim();
      if (!rowSearchResultId) continue;
      const mapped = mapSupabaseRowToResearchedContact(row);
      for (const variant of buildSearchIdVariants(rowSearchResultId)) {
        if (!researchedByVariant.has(variant)) researchedByVariant.set(variant, mapped);
      }
    }
  }

  return contacts.map((lead) => {
    const sourceId =
      (lead as { sourceSearchResultId?: unknown }).sourceSearchResultId ??
      (lead as { searchResultIdRaw?: unknown }).searchResultIdRaw ??
      (lead as { search_result_id?: unknown }).search_result_id ??
      (lead as { SearchResultId?: unknown }).SearchResultId ??
      lead.searchResultId;
    const match = buildSearchIdVariants(sourceId)
      .map((variant) => researchedByVariant.get(variant))
      .find(Boolean);
    if (!match) return lead;
    return { ...lead, researchedData: match };
  });
}

function requireApifyConfig() {
  if (!API_CONFIG.APIFY_API_TOKEN) {
    throw new Error(
      'Apify is not configured. Set VITE_APIFY_API_TOKEN in your frontend env.'
    );
  }
  return { token: API_CONFIG.APIFY_API_TOKEN, actorId: API_CONFIG.APIFY_ACTOR_ID };
}

// Search for leads
export async function searchLeads(
  filters: SearchFilters,
  page: number = 0,
  perPage: number = 25,
  options?: { userEmail?: string }
): Promise<{ contacts: Lead[]; total: number; contactSearchId: number | null; nextToken: string | null }> {
  const scope = filters.locationScope || 'both_or';
  const scopedFilters: Record<string, unknown> = { ...filters };
  if (scope === 'company') {
    scopedFilters.companyLocations = filters.locations;
    scopedFilters.contactLocations = [];
    scopedFilters.locations = filters.locations;
  } else if (scope === 'contact') {
    scopedFilters.contactLocations = filters.locations;
    scopedFilters.companyLocations = [];
    scopedFilters.locations = filters.locations;
  } else {
    scopedFilters.companyLocations = [];
    scopedFilters.contactLocations = [];
  }

  // Keep UI wording while sending Seamless-compatible job change enums.
  if (Array.isArray(filters.jobChangesType)) {
    const hasPromotion = filters.jobChangesType.includes('Promotion');
    const hasHire = filters.jobChangesType.includes('New Hire');
    if (hasPromotion && hasHire) scopedFilters.jobChangesType = ['New Company or Title'];
    else if (hasPromotion) scopedFilters.jobChangesType = ['New Title'];
    else if (hasHire) scopedFilters.jobChangesType = ['New Company'];
  }

  const response = await fetch(`${SEARCH_API_URL}/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...scopedFilters, page, perPage }),
  });

  if (!response.ok) {
    let details = '';
    try {
      const text = await response.text();
      if (text) {
        try {
          const parsed = JSON.parse(text) as { error?: string; message?: string };
          details = parsed.error || parsed.message || text;
        } catch {
          details = text;
        }
      }
    } catch {
      // ignore body parse failures
    }
    throw new Error(`Search failed (${response.status})${details ? `: ${details}` : ''}`);
  }
  const raw = (await response.json()) as {
    contacts?: Lead[];
    total?: number;
    contactSearchId?: number | null;
    nextToken?: string | null;
  };
  const seenSearchIds = new Map<string, number>();
  const contacts = Array.isArray(raw.contacts)
    ? raw.contacts.map((lead, index) => {
        const normalized = { ...lead };
        const baseId = normalizeSearchResultId(lead as unknown as Record<string, unknown>, index);
        const seen = seenSearchIds.get(baseId) ?? 0;
        seenSearchIds.set(baseId, seen + 1);
        normalized.searchResultId = seen === 0 ? baseId : `${baseId}__${seen + 1}`;
        normalized.companyName = leadCompanyName(lead);
        normalized.companyLinkedInUrl = leadCompanyLinkedIn(lead);
        normalized.linkedInUrl = lead.linkedInUrl || lead.liUrl || '';
        return normalized;
      })
    : [];
  const hydratedContacts = await hydrateResearchedDataForContacts(options?.userEmail, contacts);
  return {
    contacts: hydratedContacts,
    total: Number(raw.total ?? contacts.length),
    contactSearchId: raw.contactSearchId ?? null,
    nextToken: raw.nextToken ?? null,
  };
}

// Research a single contact
export async function researchContact(
  lead: Lead,
  userEmail: string,
  options?: {
    preserveSearchResultId?: boolean;
    extraTags?: string[];
    superScrape?: boolean;
    pepaiResearch?: boolean;
    instantlyCampaignId?: string;
    instantlyCampaignName?: string;
    targetGhlLocationId?: string;
    targetGhlUserEmail?: string;
    skipCrmSync?: boolean;
  }
): Promise<{
  success: boolean;
  data: ResearchedContact;
  crmSync?: { attempted?: boolean; status?: string; reason?: string };
  prospectAssignment?: { attempted?: boolean; status?: string; reason?: string };
  instantly?: { status?: string; error?: string };
  async?: { status: string; jobId?: string; attempts?: number; nextAttemptAt?: string | null; pollAfterMs?: number };
}> {
  const email = leadPrimaryEmail(lead);
  const name = lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || email;
  const rawSearchResultId = String(
    (lead as { searchResultId?: unknown }).searchResultId ??
    (lead as { SearchResultId?: unknown }).SearchResultId ??
    (lead as { sourceSearchResultId?: unknown }).sourceSearchResultId ??
    ''
  ).trim();
  const rawSourceSearchResultId = String(
    (lead as { sourceSearchResultId?: unknown }).sourceSearchResultId ??
    (lead as { searchResultId?: unknown }).searchResultId ??
    (lead as { SearchResultId?: unknown }).SearchResultId ??
    ''
  ).trim();
  const fallbackSearchResultId = normalizeSearchResultId(
    lead as unknown as Record<string, unknown>,
    0
  );
  const runSearchResultId = rawSearchResultId || fallbackSearchResultId;
  
  const payload = {
    companyName: leadCompanyName(lead),
    companyWebsite: lead.domain || '',
    contactName: name,
    request_name: name || undefined,
    requestName: name || undefined,
    findAllId: null,
    findSelectedId: null,
    isBatch: false,
    isWaiting: false,
    reloadNonCseContactOnResearchDone: false,
    researchSource: 'Find / Contact / All / In App / n/a / n/a',
    researchedAt: new Date().toISOString(),
    searchResultId: runSearchResultId,
    sourceSearchResultId: rawSourceSearchResultId || rawSearchResultId || runSearchResultId,
    social: {
      linkedin: lead.liUrl || lead.linkedInUrl || '',
      twitter: '',
      facebook: '',
      google: '',
    },
    title: lead.title || '',
    uiMatchId: lead.uiMatchId || `${lead.contactSearchId}-0-0`,
    contactSearchId: lead.contactSearchId,
    naicsCode: '',
    naicsDesc: '',
    userEmail,
    request_email: email || undefined,
    requestEmail: email || undefined,
    crmContext: {
      sourceType: 'manual_get_data',
      superScrape: options?.superScrape === true,
      pepaiResearch: options?.pepaiResearch === true,
      instantlyCampaignId: options?.instantlyCampaignId || undefined,
      instantlyCampaignName: options?.instantlyCampaignName || undefined,
      targetGhlLocationId: options?.targetGhlLocationId || undefined,
      ghlTargetLocationId: options?.targetGhlLocationId || undefined,
      targetGhlUserEmail: options?.targetGhlUserEmail || undefined,
      ghlTargetUserEmail: options?.targetGhlUserEmail || undefined,
      skipCrmSync: options?.skipCrmSync === true ? true : undefined,
      tags: buildManualResearchTags(lead, userEmail, options?.extraTags ?? []),
    },
    researchedData: undefined,
  };

  const response = await fetch(`${RESEARCH_API_URL}/research`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch((error: unknown) => {
    throw new Error(`Research request failed: ${error instanceof Error ? error.message : String(error)}`);
  });

  if (response.ok) return response.json();
  const details = await readErrorDetails(response);
  let lastMessage = formatHttpError('Research failed', response.status, details);
  if (isTransientResearchStatus(response.status)) {
    for (let attempt = 2; attempt <= 3; attempt += 1) {
      await sleep((attempt - 1) * 1000);
      try {
        const retryResponse = await fetch(`${RESEARCH_API_URL}/research`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (retryResponse.ok) return retryResponse.json();
        const retryDetails = await readErrorDetails(retryResponse);
        lastMessage = formatHttpError('Research failed', retryResponse.status, retryDetails);
        if (!isTransientResearchStatus(retryResponse.status)) {
          break;
        }
      } catch (error) {
        lastMessage = `Research request failed: ${error instanceof Error ? error.message : String(error)}`;
      }
    }
  }
  throw new Error(lastMessage);
}

export async function listInstantlyCampaignOptions(input?: { userEmail?: string }): Promise<{
  success: boolean;
  campaigns: InstantlyCampaignOption[];
  defaultCampaign?: InstantlyCampaignOption | null;
  error?: string;
}> {
  const params = new URLSearchParams();
  const userEmail = String(input?.userEmail ?? '').trim();
  if (userEmail) params.set('userEmail', userEmail);
  const url = `${RESEARCH_API_URL}/instantly-campaigns${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Instantly campaign list failed');
  return response.json();
}

export async function getInstantlyCampaignDetail(input: {
  userEmail: string;
  campaignId: string;
}): Promise<{
  success: boolean;
  campaign?: { id: string; name?: string; raw?: Record<string, unknown> };
  error?: string;
}> {
  const userEmail = String(input.userEmail ?? '').trim();
  const campaignId = String(input.campaignId ?? '').trim();
  if (!userEmail) throw new Error('User email is required.');
  if (!campaignId) throw new Error('Campaign id is required.');
  const params = new URLSearchParams({ userEmail, campaignId });
  const response = await fetch(`${RESEARCH_API_URL}/instantly-campaigns/detail?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Instantly campaign detail failed');
  return response.json();
}

export async function createInstantlyCampaign(input: InstantlyCampaignCreateInput): Promise<{
  success: boolean;
  campaign?: { id: string; name?: string };
  error?: string;
  raw?: Record<string, unknown>;
}> {
  const name = String(input.name ?? '').trim();
  if (!name) throw new Error('Campaign name is required.');
  const cleanSteps = Array.isArray(input.steps)
    ? input.steps
        .map((step) => ({
          subject: String(step.subject ?? '').trim(),
          body: String(step.body ?? '').trim(),
          delayDays: Number.isFinite(Number(step.delayDays)) ? Math.max(0, Math.floor(Number(step.delayDays))) : 0,
        }))
        .filter((step) => step.subject && step.body)
    : [];
  if (cleanSteps.length === 0) throw new Error('At least one email step is required.');

  const payload: Record<string, unknown> = {
    name,
    timezone: String(input.timezone ?? '').trim() || undefined,
    start_date: String(input.startDate ?? '').trim() || undefined,
    end_date: String(input.endDate ?? '').trim() || undefined,
    daily_limit: Number.isFinite(Number(input.dailyLimit)) ? Math.max(1, Math.floor(Number(input.dailyLimit))) : undefined,
    email_gap: Number.isFinite(Number(input.emailGapMinutes)) ? Math.max(0, Math.floor(Number(input.emailGapMinutes))) : undefined,
    stop_on_reply: input.stopOnReply ?? true,
    open_tracking: input.openTracking ?? true,
    link_tracking: input.linkTracking ?? true,
    sequences: [
      {
        steps: cleanSteps.map((step, index) => ({
          type: 'email',
          variant: 'A',
          step_number: index + 1,
          delay_days: step.delayDays ?? 0,
          subject: step.subject,
          body: step.body,
        })),
      },
    ],
  };

  const response = await fetch(`${RESEARCH_API_URL}/instantly-campaigns/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail: input.userEmail,
      payload,
    }),
  });
  if (!response.ok) {
    const details = await readErrorDetails(response);
    throw new Error(formatHttpError('Instantly campaign create failed', response.status, details));
  }
  return response.json();
}

export async function updateInstantlyCampaign(input: InstantlyCampaignUpdateInput): Promise<{
  success: boolean;
  campaign?: { id: string; name?: string };
  error?: string;
  raw?: Record<string, unknown>;
}> {
  const campaignId = String(input.campaignId ?? '').trim();
  if (!campaignId) throw new Error('Campaign id is required.');
  const name = String(input.name ?? '').trim();
  if (!name) throw new Error('Campaign name is required.');
  const cleanSteps = Array.isArray(input.steps)
    ? input.steps
        .map((step) => ({
          subject: String(step.subject ?? '').trim(),
          body: String(step.body ?? '').trim(),
          delayDays: Number.isFinite(Number(step.delayDays)) ? Math.max(0, Math.floor(Number(step.delayDays))) : 0,
        }))
        .filter((step) => step.subject && step.body)
    : [];
  if (cleanSteps.length === 0) throw new Error('At least one email step is required.');

  const payload: Record<string, unknown> = {
    name,
    timezone: String(input.timezone ?? '').trim() || undefined,
    start_date: String(input.startDate ?? '').trim() || undefined,
    end_date: String(input.endDate ?? '').trim() || undefined,
    daily_limit: Number.isFinite(Number(input.dailyLimit)) ? Math.max(1, Math.floor(Number(input.dailyLimit))) : undefined,
    email_gap: Number.isFinite(Number(input.emailGapMinutes)) ? Math.max(0, Math.floor(Number(input.emailGapMinutes))) : undefined,
    stop_on_reply: input.stopOnReply ?? true,
    open_tracking: input.openTracking ?? true,
    link_tracking: input.linkTracking ?? true,
    sequences: [
      {
        steps: cleanSteps.map((step, index) => ({
          type: 'email',
          variant: 'A',
          step_number: index + 1,
          delay_days: step.delayDays ?? 0,
          subject: step.subject,
          body: step.body,
        })),
      },
    ],
  };

  const response = await fetch(`${RESEARCH_API_URL}/instantly-campaigns/update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail: input.userEmail,
      campaignId,
      payload,
    }),
  });
  if (!response.ok) {
    const details = await readErrorDetails(response);
    throw new Error(formatHttpError('Instantly campaign update failed', response.status, details));
  }
  return response.json();
}

export async function syncExistingContactsToCrm(input: {
  userEmail: string;
  searchResultIds?: string[];
  contactIds?: string[];
  contacts?: Array<Record<string, unknown>>;
  companyNames?: string[];
  targetGhlLocationId?: string;
}): Promise<{
  success: boolean;
  sent?: number;
  failed?: number;
  skipped?: number;
  total?: number;
  error?: string;
}> {
  const response = await fetch(`${RESEARCH_API_URL}/crm-sync-existing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail: input.userEmail,
      searchResultIds: input.searchResultIds ?? [],
      contactIds: input.contactIds ?? [],
      contacts: input.contacts ?? [],
      companyNames: input.companyNames ?? [],
      crmContext: {
        sourceType: 'manual_existing_crm_sync',
        targetGhlLocationId: input.targetGhlLocationId || undefined,
        ghlTargetLocationId: input.targetGhlLocationId || undefined,
      },
    }),
  });
  if (!response.ok) throw new Error('CRM sync failed');
  return response.json();
}

export async function enrollExistingContactsToInstantly(input: {
  userEmail: string;
  instantlyCampaignId: string;
  instantlyCampaignName?: string;
  searchResultIds?: string[];
  contactIds?: string[];
  contacts?: Array<Record<string, unknown>>;
}): Promise<{
  success: boolean;
  sent?: number;
  failed?: number;
  skipped?: number;
  total?: number;
  error?: string;
}> {
  const response = await fetch(`${RESEARCH_API_URL}/instantly-enroll-existing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userEmail: input.userEmail,
      instantlyCampaignId: input.instantlyCampaignId,
      instantlyCampaignName: input.instantlyCampaignName || undefined,
      searchResultIds: input.searchResultIds ?? [],
      contactIds: input.contactIds ?? [],
      contacts: input.contacts ?? [],
      crmContext: {
        sourceType: 'manual_existing_instantly_enroll',
        pepaiResearch: true,
        instantlyCampaignId: input.instantlyCampaignId,
        instantlyCampaignName: input.instantlyCampaignName || undefined,
        tags: ['PEPAI-research'],
      },
    }),
  });
  if (!response.ok) throw new Error('Instantly enroll failed');
  return response.json();
}

export async function getResearchStatus(
  userEmail: string,
  jobId: string
): Promise<{
  success: boolean;
  status: string;
  stage?: string;
  stageLabel?: string;
  progress?: number;
  done: boolean;
  data?: ResearchedContact;
  attempts?: number;
  nextAttemptAt?: string | null;
  error?: string;
}> {
  const params = new URLSearchParams({ userEmail, jobId });
  const response = await fetch(`${RESEARCH_API_URL}/research/status?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Research status failed');
  return response.json();
}

// Research batch of contacts
export async function researchBatch(
  leads: Lead[],
  indices: number[],
  userEmail: string
): Promise<{
  success: boolean;
  results: Array<{ success: boolean; searchResultId: string; data: ResearchedContact }>;
  totalSaved: number;
  totalRequested: number;
}> {
  const contacts = indices.map((i) => {
    const lead = leads[i];
    const name = lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim();
    const baseSearchResultId = String(lead.searchResultId ?? '').trim() || `lead-${i}`;
    return {
      companyName: leadCompanyName(lead),
      contactName: name,
      request_name: name || undefined,
      requestName: name || undefined,
      searchResultId: baseSearchResultId,
      sourceSearchResultId: baseSearchResultId,
      social: {
        linkedin: lead.liUrl || lead.linkedInUrl || '',
        twitter: '',
        facebook: '',
        google: '',
      },
      title: lead.title || '',
      uiMatchId: lead.uiMatchId || `${lead.contactSearchId}-0-${i}`,
      naicsCode: '',
      naicsDesc: '',
      crmContext: {
        sourceType: 'manual_get_data',
        tags: buildManualResearchTags(lead, userEmail),
      },
      researchedData: undefined,
    };
  });

  const payload = {
    contacts,
    contactSearchId: leads[0]?.contactSearchId,
    findAllId: null,
    findSelectedId: null,
    isBatch: true,
    isWaiting: false,
    reloadNonCseContactOnResearchDone: false,
    researchSource: 'Find / Contact / All / In App / n/a / n/a',
    researchedAt: new Date().toISOString(),
    userEmail,
  };

  const response = await fetch(`${RESEARCH_API_URL}/researchBatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error('Batch research failed');
  return response.json();
}

// Schedule a search
export async function createScheduledSearch(schedule: ScheduledSearch): Promise<{ success: boolean }> {
  const response = await fetch(`${SEARCH_API_URL}/schedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(schedule),
  });

  if (!response.ok) throw new Error('Failed to create schedule');
  return response.json();
}

export async function createSavedSearch(payload: { userEmail: string; name: string; filters: SearchFilters; perPage?: number }): Promise<{ success: boolean; savedSearch?: SavedSearch }> {
  const response = await fetch(`${SEARCH_API_URL}/saved-search/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('Failed to save search');
  return response.json();
}

export async function listSavedSearches(userEmail: string): Promise<{ success: boolean; savedSearches: SavedSearch[] }> {
  const response = await fetch(`${SEARCH_API_URL}/saved-search/list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userEmail }),
  });
  if (!response.ok) throw new Error('Failed to load saved searches');
  const data = await response.json();
  const saved = Array.isArray(data.savedSearches) ? data.savedSearches.map((s: any) => ({
    id: s.savedSearchId || s.id,
    name: s.name,
    filters: s.filters,
    savedAt: s.savedAt,
    lastPage: s.lastPage,
    perPage: s.perPage,
  })) : [];
  return { success: Boolean(data.success), savedSearches: saved };
}

export async function updateSavedSearchPage(payload: { userEmail: string; savedSearchId: string; lastPage: number; perPage: number }): Promise<{ success: boolean }> {
  const response = await fetch(`${SEARCH_API_URL}/saved-search/update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error('Failed to update saved search');
  return response.json();
}

// LinkedIn scraping
export async function scrapeLinkedIn(
  linkedinUrl: string,
  companyLinkedinUrl?: string
): Promise<{ runId: string; datasetId: string }> {
  const { token, actorId } = requireApifyConfig();
  const urls = [linkedinUrl];
  if (companyLinkedinUrl) urls.push(companyLinkedinUrl);

  const payload = {
    targetUrls: urls,
    maxPosts: 3,
    includeQuotePosts: true,
    includeReposts: true,
    scrapeReactions: false,
    scrapeComments: false,
  };

  const response = await fetch(
    `https://api.apify.com/v2/acts/${actorId}/runs?token=${token}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) throw new Error('Failed to start LinkedIn scrape');
  const data = await response.json();
  return { runId: data.data.id, datasetId: data.data.defaultDatasetId };
}

export async function checkLinkedInScrapeStatus(runId: string): Promise<string> {
  const { token, actorId } = requireApifyConfig();
  const response = await fetch(
    `https://api.apify.com/v2/acts/${actorId}/runs/${runId}?token=${token}`
  );

  if (!response.ok) throw new Error('Failed to check scrape status');
  const data = await response.json();
  return data.data.status;
}

export async function getLinkedInScrapeResults(datasetId: string): Promise<unknown[]> {
  const { token } = requireApifyConfig();
  const response = await fetch(
    `https://api.apify.com/v2/datasets/${datasetId}/items?token=${token}`
  );

  if (!response.ok) throw new Error('Failed to get scrape results');
  return response.json();
}

// Full LinkedIn scrape with polling
export async function scrapeLinkedInWithPolling(
  linkedinUrl: string,
  companyLinkedinUrl?: string,
  onProgress?: (percent: number, status: string) => void
): Promise<{
  scrapedAt: string;
  personData: unknown[];
  companyData: unknown[];
  raw: unknown[];
}> {
  onProgress?.(10, 'Starting Apify actor...');
  const { runId, datasetId } = await scrapeLinkedIn(linkedinUrl, companyLinkedinUrl);

  onProgress?.(20, 'Scraping LinkedIn profile...');

  let status = 'RUNNING';
  let attempts = 0;
  const maxAttempts = 60;

  while (status === 'RUNNING' || status === 'READY') {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    status = await checkLinkedInScrapeStatus(runId);
    attempts++;
    const progress = Math.min(20 + attempts * 1.3, 90);
    onProgress?.(progress, `Scraping... (${attempts * 2}s)`);

    if (attempts >= maxAttempts) {
      throw new Error('Scrape timed out');
    }
  }

  if (status !== 'SUCCEEDED') {
    throw new Error(`Scrape failed with status: ${status}`);
  }

  onProgress?.(95, 'Fetching results...');
  const results = await getLinkedInScrapeResults(datasetId);

  const scrapedData = {
    scrapedAt: new Date().toISOString(),
    personData: results.filter((r) =>
      (r as { query?: { targetUrl?: string } }).query?.targetUrl?.includes('/in/')
    ),
    companyData: results.filter((r) =>
      (r as { query?: { targetUrl?: string } }).query?.targetUrl?.includes('/company/')
    ),
    raw: results,
  };

  onProgress?.(100, 'Complete!');
  return scrapedData;
}

// Export leads to CSV
export function exportLeadsToCSV(leads: Lead[], researchedContacts: Record<string, ResearchedContact>) {
  const headers = [
    'Name', 'Title', 'Company', 'Industry', 'Mobile', 'Business Phone',
    'Business Email', 'Personal Email', 'LinkedIn', 'Location'
  ];

  const rows = leads.map((l) => {
    const cached = findMatchingResearchedContact(l, researchedContacts);
    return [
      l.name || `${l.firstName || ''} ${l.lastName || ''}`.trim(),
      l.title || '',
      leadCompanyName(l),
      (typeof l.company === 'string' ? '' : l.company?.industry) || l.industry || '',
      cached?.contactPhone1 || l.contactPhone1 || '',
      cached?.companyPhone1 || l.contactPhone2 || '',
      cached?.Email || l.businessEmail1 || l.email || '',
      cached?.PersonalEmail || l.personalEmail1 || '',
      cached?.LIProfileUrl || l.linkedInUrl || '',
      l.location || l.city || l.state || '',
    ];
  });

  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${(c || '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
}
